import type { FC } from 'react'
import { useCallback, useMemo, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material'
import { validateName } from '@/common/utils'

interface ConfigurationStepProps {
  readonly initialValue: string
  readonly isGenerating: boolean
  readonly error: string | null
  readonly onGenerate: (hostName: string) => void
  readonly onBack: () => void
}

export const ConfigurationStep: FC<ConfigurationStepProps> = ({
  initialValue,
  isGenerating,
  error,
  onGenerate,
  onBack,
}) => {
  const [hostName, setHostName] = useState(initialValue)
  const [touched, setTouched] = useState(false)

  const validation = useMemo(() => validateName(hostName, 'Host name'), [hostName])

  const isFormValid = validation.isValid

  const handleHostNameChange = useCallback((event: { target: { value: string } }) => {
    setHostName(event.target.value)
    setTouched(true)
  }, [])

  const handleGenerate = useCallback(() => {
    if (isFormValid) {
      onGenerate(hostName)
    }
  }, [isFormValid, hostName, onGenerate])

  return (
    <Box sx={containerSx}>
      <Typography variant="h6" sx={titleSx}>
        Configure Your Host
      </Typography>

      <TextField
        label="Host Name"
        value={hostName}
        onChange={handleHostNameChange}
        error={touched && !validation.isValid}
        helperText={
          touched ? validation.error : 'A descriptive name for your host (e.g., My Computer)'
        }
        fullWidth
        disabled={isGenerating}
        sx={fieldSx}
        autoFocus
      />

      <Typography variant="body2" sx={hintSx}>
        Use any combination of letters, numbers, spaces, hyphens, and underscores
      </Typography>

      {error && (
        <Alert severity="error" sx={alertSx}>
          {error}
        </Alert>
      )}

      <Box sx={actionsSx}>
        <Button onClick={onBack} disabled={isGenerating}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={!isFormValid || isGenerating}
          startIcon={isGenerating ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isGenerating ? 'Generating...' : 'Generate Host ID'}
        </Button>
      </Box>
    </Box>
  )
}

const containerSx: SxProps<Theme> = {
  py: 2,
}

const titleSx: SxProps<Theme> = {
  fontWeight: 600,
  mb: 3,
}

const fieldSx: SxProps<Theme> = {
  mb: 3,
}

const hintSx: SxProps<Theme> = {
  color: 'text.secondary',
  textAlign: 'center',
  mb: 2,
}

const alertSx: SxProps<Theme> = {
  mb: 2,
}

const actionsSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  mt: 4,
}
